import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { useAuth } from "@/lib/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { lang } = useLang();
  const { user } = useAuth();

  if (!user || !user.id) return null;

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20 max-w-3xl" data-testid="settings-page">
      <p className="label-cap">— {lang === "pt" ? "A sua conta" : "Your account"}</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="settings-title">
        {lang === "pt" ? "Definições" : "Settings"}
      </h1>

      <section className="mt-12 border border-[var(--border)] p-8" data-testid="settings-profile">
        <p className="label-cap text-[var(--ink-soft)]">— {lang === "pt" ? "Perfil" : "Profile"}</p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label={lang === "pt" ? "Nome" : "Name"} value={user.name} />
          <Field label="Email" value={user.email} />
          <Field
            label={lang === "pt" ? "Estado da subscrição" : "Subscription status"}
            value={user.subscription_status || "—"}
          />
          <Field label={lang === "pt" ? "Idioma" : "Language"} value={user.language || "pt"} />
        </div>
      </section>

      <DangerZone lang={lang} />
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="label-cap text-[var(--ink-faint)]">{label}</p>
      <p className="font-display text-lg mt-1 break-words">{value}</p>
    </div>
  );
}

function DangerZone({ lang }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ loading: false, error: "", success: null });

  const expected = lang === "pt" ? "ELIMINAR" : "DELETE";

  const submit = async (e) => {
    e.preventDefault();
    if (confirm.trim().toUpperCase() !== expected) {
      setState({
        loading: false,
        error: lang === "pt"
          ? `Para confirmar, escreva exactamente «${expected}».`
          : `To confirm, type «${expected}».`,
        success: null,
      });
      return;
    }
    setState({ loading: true, error: "", success: null });
    try {
      const token = localStorage.getItem("mordomo_token");
      const r = await fetch(`${BACKEND_URL}/api/account`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ password, reason: reason.trim() || null, lang }),
      });
      const body = await r.json();
      if (!r.ok) {
        const detail = Array.isArray(body?.detail) ? body.detail[0]?.msg : body?.detail;
        throw new Error(detail || (lang === "pt" ? "Pedido falhou." : "Request failed."));
      }
      setState({ loading: false, error: "", success: body });
    } catch (err) {
      setState({ loading: false, error: err.message, success: null });
    }
  };

  const finish = async () => {
    await logout();
    navigate("/");
  };

  if (state.success) {
    return (
      <section
        className="mt-10 border border-[var(--ink)] p-8 bg-[var(--bg-soft)]"
        data-testid="settings-deletion-success"
      >
        <p className="label-cap text-[var(--oxblood)]">— {lang === "pt" ? "Conta eliminada" : "Account deleted"}</p>
        <h2 className="font-display text-2xl font-light mt-2">
          {lang === "pt" ? "Está feito, Chefe." : "It is done, Chefe."}
        </h2>
        <p className="mt-4 text-[15px] leading-relaxed">{state.success.message}</p>
        <p className="mt-3 text-sm text-[var(--ink-soft)]">
          {lang === "pt" ? "Referência: " : "Reference: "}
          <code data-testid="settings-deletion-ref">{state.success.request_id}</code>
        </p>
        <button
          type="button"
          onClick={finish}
          className="btn-primary text-sm mt-6"
          data-testid="settings-deletion-logout"
        >
          {lang === "pt" ? "Terminar sessão agora" : "Sign out now"}
        </button>
      </section>
    );
  }

  return (
    <section className="mt-10 border border-[var(--oxblood)] p-8" data-testid="settings-danger-zone">
      <p className="label-cap text-[var(--oxblood)]">— {lang === "pt" ? "Zona delicada" : "Sensitive zone"}</p>
      <h2 className="font-display text-2xl font-light mt-2">
        {lang === "pt" ? "Eliminar a sua conta" : "Delete your account"}
      </h2>
      <p className="mt-3 text-[15px] leading-relaxed text-[var(--ink-soft)]">
        {lang === "pt"
          ? "Eliminação imediata e irreversível: apaga perfil, conversas, Memória, eventos, medicação, consultas, acordos digitais, Diário e a ligação ao Gmail (acesso revogado). Registos contabilísticos mantidos por obrigação legal (10 anos, pseudonimizados)."
          : "Immediate and irreversible: erases profile, conversations, Memory, events, medications, appointments, agreements, Diary and the Gmail connection (access revoked). Accounting records kept for 10 years (pseudonymised) by law."}
      </p>

      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="btn-ghost text-sm mt-5 text-[var(--oxblood)] border border-[var(--oxblood)]"
          data-testid="settings-deletion-open"
        >
          {lang === "pt" ? "Quero eliminar a minha conta" : "I want to delete my account"}
        </button>
      )}

      {open && (
        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="settings-deletion-form">
          <p className="text-sm text-[var(--oxblood)] leading-relaxed">
            {lang === "pt"
              ? "Atenção: esta acção é imediata e não pode ser desfeita. Todos os seus dados serão apagados agora."
              : "Warning: this action is immediate and cannot be undone. All your data will be erased now."}
          </p>
          <div>
            <label className="label-cap block mb-2" htmlFor="settings-deletion-reason">
              {lang === "pt" ? "Motivo (opcional)" : "Reason (optional)"}
            </label>
            <textarea
              id="settings-deletion-reason"
              rows={3}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={lang === "pt"
                ? "Se quiser dizer porquê — ajuda-nos a melhorar, sem obrigação."
                : "Optional — helps us improve."}
              className="w-full bg-[var(--bg)] border border-[var(--border)] px-4 py-3 text-[15px] focus:outline-none focus:border-[var(--ink)]"
              data-testid="settings-deletion-reason"
            />
          </div>

          <div>
            <label className="label-cap block mb-2" htmlFor="settings-deletion-confirm">
              {lang === "pt" ? (
                <>Para confirmar, escreva <strong>ELIMINAR</strong></>
              ) : (
                <>To confirm, type <strong>DELETE</strong></>
              )}
            </label>
            <input
              id="settings-deletion-confirm"
              type="text"
              required
              autoComplete="off"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={lang === "pt" ? "ELIMINAR" : "DELETE"}
              className="w-full bg-[var(--bg)] border border-[var(--oxblood)] px-4 py-3 text-[15px] tracking-widest focus:outline-none"
              data-testid="settings-deletion-confirm"
            />
          </div>

          <div>
            <label className="label-cap block mb-2" htmlFor="settings-deletion-password">
              {lang === "pt" ? "A sua palavra-passe" : "Your password"}
            </label>
            <input
              id="settings-deletion-password"
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[var(--bg)] border border-[var(--oxblood)] px-4 py-3 text-[15px] focus:outline-none"
              data-testid="settings-deletion-password"
            />
          </div>

          {state.error && (
            <p className="text-sm text-[var(--oxblood)]" data-testid="settings-deletion-error">{state.error}</p>
          )}

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirm(""); setPassword(""); setReason(""); setState({ loading: false, error: "", success: null }); }}
              className="btn-ghost text-sm"
              data-testid="settings-deletion-cancel"
            >
              {lang === "pt" ? "Cancelar" : "Cancel"}
            </button>
            <button
              type="submit"
              disabled={state.loading || !confirm || !password}
              className="btn-primary text-sm bg-[var(--oxblood)] text-[var(--bg)] disabled:opacity-50"
              data-testid="settings-deletion-submit"
            >
              {state.loading
                ? (lang === "pt" ? "A eliminar…" : "Deleting…")
                : (lang === "pt" ? "Eliminar definitivamente" : "Delete permanently")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
